/**
 * Mindmap View - Interaction Module
 *
 * Mouse/touch/keyboard event handlers for nodes.
 * Uses callback injection pattern for dependencies.
 */

import * as State from './state.js';
import { isMobile } from '../../../../shared/utils/device-detect.js';

// ============================================================
// Module callbacks (injected via init)
// ============================================================
let interactionCallbacks = null;

/**
 * Initialize interaction module with callbacks
 * @param {Object} cbs
 * @param {Function} cbs.onNodeSelect - Select node
 * @param {Function} cbs.onNodeEdit - Edit node inline
 * @param {Function} cbs.onNodeContextMenu - Show context menu
 * @param {Function} cbs.onOpenModal - Open properties modal
 * @param {Function} cbs.onNavigate - Navigate to node
 * @param {Function} cbs.isReadOnly - Check read-only status
 * @param {Function} cbs.zoomTo - Zoom to node
 */
export function init(cbs) {
  interactionCallbacks = cbs;
}

/**
 * Setup interaction handlers on a node element
 * Called by render.js when creating nodes. The element may be kept across
 * renders: `el._mmNode` is the node as last rendered (ids never change).
 * @param {HTMLElement} el - The node DOM element
 * @param {Object} node - The node data
 * @param {boolean} isRoot - Whether this is the root node
 */
export function setupNodeInteraction(el, node, isRoot) {
  // Hover tracking — desktop only (mouseenter fires on tap on iOS Safari, wasted work).
  if (!isMobile()) {
    el.addEventListener("mouseenter", () => {
      State.setHoveredMindmapId(node.id);
    });

    el.addEventListener("mouseleave", () => {
      if (State.hoveredMindmapId === node.id) {
        State.setHoveredMindmapId(null);
      }
    });
  }

  // Single click to select
  el.addEventListener("click", (e) => {
    e.stopPropagation();
    if (!e.target.classList.contains("mm-toggle") && !el._justDragged) {
      interactionCallbacks?.onNodeSelect?.(node.id);
    }
    el._justDragged = false;
  });

  // Double click to zoom into subtree (but not on title or toggle)
  el.addEventListener("dblclick", (e) => {
    e.stopPropagation();
    const isToggle = e.target.classList.contains("mm-toggle");
    const isTitle = e.target.classList.contains("mm-title");
    if (!isToggle && !isTitle && !el._justDragged) {
      interactionCallbacks?.zoomTo?.(node.id);
    }
  });

  // Right click for context menu
  el.addEventListener("contextmenu", (e) => {
    e.preventDefault();
    e.stopPropagation();
    interactionCallbacks?.onNodeContextMenu?.(node.id, e.clientX, e.clientY, el._mmNode || node);
  });
}

/**
 * Setup title-specific interactions (double-click to edit)
 * @param {HTMLElement} titleSpan - The title span element
 * @param {Object} node - The node data
 * @param {boolean} isViewer - Whether user is read-only viewer
 */
export function setupTitleInteraction(titleSpan, node, isViewer) {
  titleSpan.addEventListener("dblclick", (e) => {
    e.stopPropagation();
    e.preventDefault();
    // Block inline editing for read-only viewers
    if (isViewer) return;
    console.log(
      "[Mindmap] Title dblclick:",
      node.id,
      "callbacks:",
      !!interactionCallbacks,
      "onNodeEdit:",
      !!interactionCallbacks?.onNodeEdit
    );
    interactionCallbacks?.onNodeEdit?.(node.id, e.clientX, e.clientY);
  });
}

/**
 * Setup notes badge interaction
 * @param {HTMLElement} badge - The notes badge element
 * @param {Object} node - The node data
 */
export function setupNotesBadgeInteraction(badge, node) {
  badge.addEventListener("click", (e) => {
    e.stopPropagation();
    interactionCallbacks?.onOpenModal?.(node.id);
  });
}

/**
 * Setup toggle button interaction
 * @param {HTMLElement} toggleBtn - The toggle button element
 * @param {HTMLElement} el - The parent node element
 * @param {Object} node - The node data
 * @param {Function} onToggle - Callback when toggled
 */
export function setupToggleInteraction(toggleBtn, el, node, onToggle) {
  toggleBtn.onclick = (e) => {
    e.stopPropagation();
    // The element may be kept across renders: the node as last rendered.
    const current = el._mmNode || node;
    current.collapsed = !current.collapsed;
    // Update the collapsed class
    el.classList.toggle("collapsed", current.collapsed);
    toggleBtn.textContent = current.collapsed ? "+" : "-";
    onToggle?.(current.id, { collapsed: current.collapsed });
  };
}
